import Workspace from './models/Workspace'
import DataLoader from 'dataloader'
import { CursorPaged, PrimeProps, Id } from 'types/definitions'
import { IAMAPI } from './API'
import {
  FindWorkspacesQuery,
  CreateWorkspacePayload,
  UpdateWorkspacePayload,
  WorkspaceDTO,
} from './dtos/workspace'
import { collection } from '../infra/collection'
import { NotFound } from 'fejl'

/**
 * Prime Workspace function.
 */
export type PrimeWorkspace = (
  props: PrimeProps<WorkspaceDTO, 'id'>,
) => Workspace | undefined

/**
 * Prime Full Workspace function.
 */
export type PrimeFullWorkspace = (workspaceDto: WorkspaceDTO) => Workspace

/**
 * Function for fetching a workspace.
 */
export type FetchWorkspace = (id: Id) => Promise<Workspace | null>

/**
 *
 */
export default class WorkspaceStore {
  private workspaceLoaderCache = new Map()

  workspaces = collection<WorkspaceDTO, Workspace>({
    create: () => new Workspace(),
  })

  workspaceLoader = new DataLoader<string, Workspace | null>(
    this._fetchMany.bind(this),
    {
      cacheMap: this.workspaceLoaderCache,
    },
  )

  /**
   * Constructor.
   */
  constructor(private api: IAMAPI) {
    this.getWorkspaceByIdOrSlug = this.getWorkspaceByIdOrSlug.bind(this)
  }

  /**
   * Gets a workspace by ID or slug from the internal collection.
   * @param id
   */
  getWorkspaceByIdOrSlug(id: string | undefined): Workspace | undefined {
    if (!id) {
      return undefined
    }
    return (
      this.workspaces.find((x) => x.slug === id || x.id === id) || undefined
    )
  }

  /**
   * Primes the cache with the workspace if we don't already have one.
   *
   * @param workspaceDto
   */
  primeFullWorkspace: PrimeFullWorkspace = (workspaceDto) => {
    if (this.workspaceLoaderCache.has(workspaceDto.id)) {
      return this.workspaces.set({ id: workspaceDto.id })
    }

    const workspace = this.workspaces.set(workspaceDto)
    this.workspaceLoader.prime(workspaceDto.id, workspace)
    return workspace
  }

  /**
   * Fetches the workspace if it hasn't been done already.
   * Returns immediately.
   */
  primeWorkspace: PrimeWorkspace = (props) => {
    if (!props || !props.id) {
      // console.warn(
      //   new Error(
      //     'WorkspaceStore.primeWorkspace: props.id is required. Returning undefined'
      //   )
      // )
      return undefined
    }

    const alreadyCached = this.workspaceLoaderCache.has(props.id)
    this.fetch(props.id)
    const workspace = this.workspaces.set({ id: props.id })
    if (alreadyCached) {
      // The workspace has already been requested, so don't
      // assign any props to it
      return workspace
    }
    return workspace.set(props, {
      stripUndefined: true,
      parse: true,
    })
  }

  fetch: FetchWorkspace = (id: Id) => this.workspaceLoader.load(Id.assert(id))

  clearFetchCacheFor(workspaceId: string): WorkspaceStore {
    this.workspaceLoader.clear(workspaceId)
    return this
  }

  async fetchWorkspace(slug: string) {
    return this.api.workspaces
      .get(slug)
      .then((data: WorkspaceDTO) => (data ? this.workspaces.set(data) : null))
  }

  async delete(id: string, force: boolean) {
    return this.api.workspaces.delete(id, { force_archive: force })
  }

  async find(query: FindWorkspacesQuery): Promise<CursorPaged<Workspace>> {
    return this.api.workspaces
      .find(query)
      .then((r) => CursorPaged.map(r, this.workspaces.set))
  }

  async create(data: CreateWorkspacePayload) {
    return this.api.workspaces
      .create(data)
      .then((data) => this.workspaces.set(data))
  }

  async update(id: string, data: UpdateWorkspacePayload) {
    return this.api.workspaces
      .update(id, data)
      .then((data) => this.workspaces.set(data))
  }

  async getArchivalStatus(id: string) {
    try {
      const workspace = await this.api.workspaces.get(id)
      return workspace.archival_status
    } catch (err) {
      if (err instanceof NotFound) {
        const status = err.archive_status
        if (status === 'ARCHIVED') {
          this.workspaces.remove(id)
        }
        return status
      }
    }
  }

  private async _fetchMany(ids: readonly string[]) {
    const dtos = await this.api.workspaces.getMany(ids)
    dtos.forEach((dto, i) => {
      const id = ids[i]
      if (!dto && id) {
        this.workspaceLoader.clear(id)
      }
    })
    return this.workspaces.set(dtos)
  }
}
